import {
  Box,
  Button,
  FormControlLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ContractListContext } from '.';
import DateRangePicker, { DateRangePickerPreset, DateRangePickerPresetUnit } from '../../components/DateRangePicker';
import SearchTextField from '../../components/SearchTextField';
import TableSkeleton from '../../components/TableSkeleton';
import { useTransferContracts } from '../../hooks/queries/useTransferContract';
import { useQueryStringState } from '../../hooks/useQueryStringState';
import useSnackbar from '../../hooks/useSnackbar';
import useTablePagination from '../../hooks/useTablePagination';
import { downloadTransferContracts, TransferContractsOrder, TransferContractsSearchType } from '../../services/api/TransferContract';
import settings from '../../services/settings';
import { DateString, IMinimumGuarantee, ITransferContract, TransferContractDealType } from '../../services/types';
import { downloadCSVFromText } from '../../services/utils';
import { RouteNames } from '../config';

interface IProps {
  isToBeTerminated?: boolean;
  onEditClick: (contract: ITransferContract) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  tableHeader: {
    verticalAlign: 'middle',
  },

  tableRow: {
    height: 52,
  },

  radioButtonGroups: {
    marginTop: 16,
    marginBottom: 20,
  },

  highlightedRow: {
    animation: '$fadeout 7s ease-in-out',
    animationDelay: '0.2s',
    animationIterationCount: 1,
  },

  '@keyframes fadeout': {
    ['0%']: {
      backgroundColor: `${theme.palette.secondary.main}33`,
    },
    ['70%']: {
      backgroundColor: `${theme.palette.secondary.main}33`,
    },
    ['100%']: {
      backgroundColor: 'none',
    },
  },

  link: {
    color: theme.palette.primary.main,
  },
}));

const DateRangePreset: DateRangePickerPreset[] = [
  {
    label: 'This month',
    unit: DateRangePickerPresetUnit.Month,
    amount: 1,
    clampOnFirstDayOfUnit: true,
  },
  { label: 'Last 3 months', unit: DateRangePickerPresetUnit.Month, amount: 3 },
  { label: 'Last 6 months', unit: DateRangePickerPresetUnit.Month, amount: 6 },
];

export const getFormatedDealTypeAndAmount = (dealType?: TransferContractDealType, value?: string | number): [string, string] => {
  if (dealType === undefined || value === undefined) {
    return ['', ''];
  }

  switch (dealType) {
    case TransferContractDealType.FlatFee:
      return ['Flat fee', `$${value.toLocaleString()}`];
    case TransferContractDealType.RevenueShare:
      return ['Revenue share', `${value}%`];
    case TransferContractDealType.MinimumGuarantee:
      return ['M.G.', `${value}%`];
    case TransferContractDealType.FixedCoinPrice:
    default:
      return [
        'Fixed coin price',
        `$${value.toLocaleString('en-US', {
          maximumFractionDigits: 5,
        })}`,
      ];
  }
};

const getAdvancePayment = (dealType: TransferContractDealType, minimumGuarantee?: Partial<IMinimumGuarantee>): string => {
  if (dealType !== TransferContractDealType.MinimumGuarantee || !minimumGuarantee?.advancePayment) {
    return '-';
  }

  const { advancePayment, shareDuring } = minimumGuarantee;
  let result = `$${advancePayment.toLocaleString()}`;

  if (shareDuring) {
    result += `(${shareDuring}%)`;
  }

  return result;
};

const ContractList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const { isToBeTerminated = false, onEditClick } = props;

  const [startDate, setStartDate] = useQueryStringState<DateString>(
    'startDate',
    isToBeTerminated ? moment().startOf('day').format() : moment().startOf('month').format(),
  );

  const [endDate, setEndDate] = useQueryStringState<DateString>(
    'endDate',
    isToBeTerminated ? moment().add(1, 'month').startOf('day').format() : moment().format(),
  );
  const [dealType, setDealType] = useQueryStringState<TransferContractDealType>('dealType');
  const [searchType, setSearchType] = useQueryStringState<TransferContractsSearchType>('searchType', TransferContractsSearchType.Username);
  const [searchKey, setSearchKey] = useQueryStringState<string>('searchKey');
  const [order, setOrder] = useQueryStringState<TransferContractsOrder>(
    'order',
    isToBeTerminated ? TransferContractsOrder.Oldest : TransferContractsOrder.Newest,
  );

  const { focusIdOnCreate, setFocusIdOnCreate = () => {} } = useContext(ContractListContext);
  const focusRowRef = useRef<HTMLTableRowElement | null>();

  const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination(1, 50);

  const { data, isLoading } = useTransferContracts(page, rowsPerPage, {
    isToBeTerminated,
    startDate,
    endDate,
    order,
    dealType,
    searchType: searchKey ? searchType : undefined,
    searchKey,
  });

  const contracts = data?.items;
  const totalCount = data?.totalCount;

  const handleDateRangeSelect = (start: Date, end: Date) => {
    setStartDate(moment(start).format('YYYY-MM-DD'));
    setEndDate(moment(end).format('YYYY-MM-DD'));
    setPage(1);
  };

  const handleDealTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDealType(value !== 'all' ? (value as TransferContractDealType) : undefined);
    setPage(1);
  };

  const handleSearchTypeChange = (e: React.ChangeEvent<any>) => {
    setSearchType(e.target.value as TransferContractsSearchType);
  };

  const handleOrderChange = (e: React.ChangeEvent<any>) => {
    setOrder(e.target.value as TransferContractsOrder);
    setPage(1);
  };

  const handleSearchKeySubmit = (value: string) => {
    setSearchKey(value);
    setPage(1);
  };

  const handleSearchKeyCancel = () => {
    setSearchKey(undefined);
  };

  const handleDownloadClick = async () => {
    try {
      const { status, data } = await downloadTransferContracts({
        isToBeTerminated,
        startDate,
        endDate,
        order,
        dealType,
        searchType: searchKey ? searchType : undefined,
        searchKey,
      });

      if (status === 200) {
        downloadCSVFromText(data, `Contracts_${moment(startDate).format('YYYYMMDD')}_${moment(endDate).format('YYYYMMDD')}`);
      } else {
        throw new Error('Unable to download a CSV file.');
      }
    } catch (e) {
      snackbar.error(e);
    }
  };

  const handleEditClick = (contract: ITransferContract) => {
    onEditClick(contract);
  };

  const handleFocusAnimationEnd = () => {
    setFocusIdOnCreate(undefined);
  };

  const renderTablePagination = () => (
    <Box display="flex" justifyContent="flex-end">
      <Select value={order} disableUnderline={true} onChange={handleOrderChange}>
        <MenuItem value={TransferContractsOrder.Newest}>Newest</MenuItem>
        <MenuItem value={TransferContractsOrder.Oldest}>Oldest</MenuItem>
      </Select>
      <TablePagination
        count={totalCount || -1} // an unknown number of items, provide -1. (https://material-ui.com/api/table-pagination/)
        rowsPerPage={rowsPerPage}
        page={page - 1} // TablePagination has the zero-based index of the current page.
        onChangePage={(_: unknown, p: number) => setPage(p + 1)}
        onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
          const rpp = parseInt(e.target.value, 10);
          setRowsPerPage(rpp);
          setPage(1);
        }}
      />
    </Box>
  );

  useEffect(() => {
    if (focusIdOnCreate && focusRowRef.current) {
      const rowOffset = focusRowRef.current.getBoundingClientRect().y;
      window.scrollTo(0, rowOffset - window.innerHeight / 2);
    }
  }, [focusIdOnCreate, contracts]);

  return (
    <Box>
      <Box marginTop={1} marginBottom={2}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeSelect}
          preset={DateRangePreset}
          hidePreset={isToBeTerminated}
          disableStartDate={isToBeTerminated}
        />
        <RadioGroup row value={dealType || 'all'} onChange={handleDealTypeChange} className={classes.radioButtonGroups}>
          <FormControlLabel value={'all'} control={<Radio color="primary" />} label="All" />
          <FormControlLabel value={TransferContractDealType.RevenueShare} control={<Radio color="primary" />} label="Revenue share" />
          <FormControlLabel value={TransferContractDealType.MinimumGuarantee} control={<Radio color="primary" />} label="Minimum guarantee" />
          <FormControlLabel value={TransferContractDealType.FlatFee} control={<Radio color="primary" />} label="Flat fee" />
          <FormControlLabel value={TransferContractDealType.FixedCoinPrice} control={<Radio color="primary" />} label="Fixed coin price" />
        </RadioGroup>
        <Box display="flex" alignItems="center">
          <Box flex="1">
            <Select value={searchType} onChange={handleSearchTypeChange}>
              <MenuItem value={TransferContractsSearchType.Username}>Username</MenuItem>
              <MenuItem value={TransferContractsSearchType.StoryTitle}>Story Title</MenuItem>
              <MenuItem value={TransferContractsSearchType.StoryId}>Story Id</MenuItem>
              <MenuItem value={TransferContractsSearchType.SeasonId}>Season Id</MenuItem>
              <MenuItem value={TransferContractsSearchType.WriterName}>Writer&#39;s Name</MenuItem>
              <MenuItem value={TransferContractsSearchType.UserId}>User Id</MenuItem>
            </Select>
            <SearchTextField
              defaultValue={searchKey}
              onValueSubmit={handleSearchKeySubmit}
              onCancel={handleSearchKeyCancel}
              isSearching={isLoading}
            />
          </Box>
          <Button variant="outlined" size="small" color="primary" onClick={handleDownloadClick}>
            Download CSV
          </Button>
        </Box>
      </Box>
      <Paper>
        {renderTablePagination()}
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableCell align="center">Original Story Title</TableCell>
            <TableCell align="center">Story Title</TableCell>
            <TableCell align="center">Story Id</TableCell>
            <TableCell align="center">Season</TableCell>
            <TableCell align="center">Season Id</TableCell>
            <TableCell align="center">Writer&#39;s Name</TableCell>
            <TableCell align="center">Username (Payment link)</TableCell>
            <TableCell align="center">Writer&apos;s account (User ID)</TableCell>
            <TableCell align="center">
              (License)
              <br />
              Effective Date
            </TableCell>
            <TableCell align="center">
              (License)
              <br />
              End Date
            </TableCell>
            <TableCell align="center">Deal Type</TableCell>
            <TableCell align="center">Advance Payment</TableCell>
            <TableCell align="center">Deal Amount</TableCell>
            <TableCell align="center">Edit</TableCell>
          </TableHead>
          <TableBody>
            {isLoading && <TableSkeleton rows={rowsPerPage} columns={14} />}
            {!isLoading &&
              contracts &&
              contracts.map(contract => {
                const { id, startsAt, endsAt, dealType, value, originalStoryTitle, minimumGuarantee } = contract;
                const story = contract.story || contract.season?.story || undefined;
                const season = contract.season || contract.story?.firstSeason || undefined;
                const seasonTitle = contract.seasonId ? season?.title || `Season ${season?.seasonNo}` : 'All Seasons';
                const writer = contract.writer;

                const penname = story?.penname || writer?.representPenname;
                const paymentWriter = season?.paymentWriter || writer;

                const [formattedDealType, formattedDealAmount] = getFormatedDealTypeAndAmount(dealType, value);

                return (
                  <TableRow
                    key={id}
                    hover
                    className={cx({
                      [classes.tableRow]: true,
                      [classes.highlightedRow]: id === Number(focusIdOnCreate),
                    })}
                    onAnimationEnd={handleFocusAnimationEnd}
                    ref={ref => {
                      if (id === Number(focusIdOnCreate)) {
                        focusRowRef.current = ref;
                      }
                    }}
                  >
                    <TableCell>{originalStoryTitle}</TableCell>
                    <TableCell>
                      {story ? (
                        <MuiLink href={`${settings.getCrewURI()}/stories/${story.id}`} target="_blank" underline="always">
                          {story.title}
                        </MuiLink>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">{story?.id || '-'}</TableCell>
                    <TableCell>
                      {story && season ? (
                        <MuiLink href={`${settings.getCrewURI()}/stories/${story.id}/seasons/${season.id}`} target="_blank" underline="always">
                          {seasonTitle}
                        </MuiLink>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">{contract.seasonId || '-'}</TableCell>
                    <TableCell>
                      {penname ? (
                        <MuiLink href={`/penname/v/${penname.id}`} target="_blank" underline="always">
                          {penname.name}
                        </MuiLink>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {paymentWriter && (
                        <MuiLink href={`${settings.getCrewURI()}/users/${paymentWriter.id}`} target="_blank" underline="always">
                          {paymentWriter.username}
                        </MuiLink>
                      )}
                    </TableCell>
                    <TableCell>
                      {writer ? (
                        <Link to={`/writer-account/v/${writer.id}/contract-history`} className={classes.link}>
                          {writer.username} ({writer.id})
                        </Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">{moment(startsAt).format('LL')}</TableCell>
                    <TableCell align="center">{moment(endsAt).format('LL')}</TableCell>
                    <TableCell align="center">{formattedDealType}</TableCell>
                    <TableCell align="right">{getAdvancePayment(dealType, minimumGuarantee)}</TableCell>
                    <TableCell align="right">{formattedDealAmount}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditClick(contract)}
                        component={Link}
                        to={`${RouteNames.Payment}/v/${contract.id}`}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>

        {!isLoading && isEmpty(contracts) && (
          <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body1">The contract does not exist. Please try again.</Typography>
          </Box>
        )}
        {renderTablePagination()}
      </Paper>
    </Box>
  );
};

export default ContractList;
